/**
 * 手绘模态框组件 - 弹窗中的绘画界面
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import { toast } from 'react-toastify';
import { nanoid } from 'nanoid';
import ApiService from '../../../api/ApiService';
import './DrawingModal.css';

const apiService = new ApiService();

export const DrawingModal = ({ 
  isOpen, 
  onClose, 
  initialPaths = [], 
  initialTextAnnotations = [],
  onSave,
  canvasWidth = 600,
  canvasHeight = 450,
}) => {
  const canvasRef = useRef(null);
  const textInputRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [eraserWidth, setEraserWidth] = useState(10);
  const [tool, setTool] = useState('pen'); // 'pen' | 'eraser' | 'text'
  const [textSize, setTextSize] = useState(24);
  const [textAnnotations, setTextAnnotations] = useState([]);
  const [activeText, setActiveText] = useState(null);
  const [textInputValue, setTextInputValue] = useState('');

  // 预设颜色
  const presetColors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080',
    '#FFC0CB', '#A52A2A', '#808080', '#00CED1', '#FFD700'
  ];

  // 根据当前工具动态调整滑块参数
  const sliderValue = tool === 'pen' ? strokeWidth : tool === 'eraser' ? eraserWidth : textSize;
  const sliderLabel = tool === 'pen'
    ? `粗细：${strokeWidth}px`
    : tool === 'eraser'
      ? `大小：${eraserWidth}px`
      : `字号：${textSize}px`;
  const sliderMin = tool === 'pen' ? 1 : tool === 'eraser' ? 10 : 12;
  const sliderMax = tool === 'pen' ? 30 : tool === 'eraser' ? 60 : 72;

  // 加载初始路径
  useEffect(() => {
    if (isOpen && initialPaths && initialPaths.length > 0 && canvasRef.current) {
      try {
        canvasRef.current.clearCanvas();
        canvasRef.current.loadPaths(initialPaths);
      } catch (err) {
        console.error('加载绘图数据失败:', err);
      }
    }
  }, [isOpen, initialPaths]);

  // 加载初始文字注释
  useEffect(() => {
    if (isOpen) {
      setTextAnnotations(initialTextAnnotations || []);
      setActiveText(null);
      setTextInputValue('');
    }
  }, [isOpen, initialTextAnnotations]);

  // 阻止模态框背景滚动
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 文本输入聚焦
  useEffect(() => {
    if (activeText && textInputRef.current) {
      const inputEl = textInputRef.current;
      inputEl.focus();
      const length = inputEl.value.length;
      inputEl.setSelectionRange(length, length);
    }
  }, [activeText]);

  const commitActiveText = useCallback(() => {
    if (!activeText) {
      return textAnnotations;
    }

    const trimmed = textInputValue.trim();
    let updatedAnnotations;

    if (!trimmed) {
      updatedAnnotations = textAnnotations.filter((item) => item.id !== activeText.id);
    } else {
      const updatedAnnotation = {
        ...activeText,
        text: trimmed,
      };
      updatedAnnotations = [
        ...textAnnotations.filter((item) => item.id !== updatedAnnotation.id),
        updatedAnnotation,
      ];
    }

    setTextAnnotations(updatedAnnotations);
    setActiveText(null);
    setTextInputValue('');
    return updatedAnnotations;
  }, [activeText, textAnnotations, textInputValue]);

  if (!isOpen) return null;

  // 撤销
  const handleUndo = () => {
    if (canvasRef.current) {
      canvasRef.current.undo();
    }
  };

  // 重做
  const handleRedo = () => {
    if (canvasRef.current) {
      canvasRef.current.redo();
    }
  };

  // 清空画布
  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clearCanvas();
      toast.success('画布已清空');
    }

    setTextAnnotations([]);
    setActiveText(null);
    setTextInputValue('');
  };

  // 将 base64 数据 URL 转换为 File 对象
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while(n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // 保存并关闭
  const handleSave = async () => {
    if (!canvasRef.current) return;
    
    try {
      const finalAnnotations = activeText ? commitActiveText() : textAnnotations;
      // 获取路径数据
      const paths = await canvasRef.current.exportPaths();
      
      // 导出为图片
      const imageData = await canvasRef.current.exportImage('png');

      const mergedImage = await mergeDrawingWithText(imageData, finalAnnotations);
      
      const hasContent = paths.length > 0 || finalAnnotations.length > 0;
      
      // 如果有内容，则上传图片到云端
      let uploadedImageUrl = mergedImage; // 默认使用本地 base64
      let fileKey = null;
      
      if (hasContent) {
        try {
          // 将 base64 转换为 File 对象
          const filename = `drawing-${Date.now()}.png`;
          const file = dataURLtoFile(mergedImage, filename);
          
          // 上传到 R2
          const uploadResult = await apiService.uploadImageToR2(file);
          
          if (uploadResult.success && uploadResult.public_url) {
            uploadedImageUrl = uploadResult.public_url;
            fileKey = uploadResult.file_key;
            console.log('手绘图片上传成功:', uploadedImageUrl);
          } else {
            throw new Error('上传返回数据不完整');
          }
        } catch (uploadError) {
          console.error('上传图片失败:', uploadError);
          // 继续保存，但使用本地 base64
        }
      }
      
      // 回调保存
      onSave({
        imageUrl: uploadedImageUrl,
        paths: paths,
        textAnnotations: finalAnnotations,
        hasContent: hasContent,
        fileKey: fileKey,
        isLocalPreview: !fileKey, // 标记是否为本地预览
      });
      
      onClose();
    } catch (err) {
      console.error('保存绘图失败:', err);
      toast.error('保存失败，请重试');
    }
  };

  // 导出图片
  const handleExport = async () => {
    if (!canvasRef.current) return;
    
    try {
      const finalAnnotations = activeText ? commitActiveText() : textAnnotations;
      const imageData = await canvasRef.current.exportImage('png');
      const mergedImage = await mergeDrawingWithText(imageData, finalAnnotations);
      
      // 创建下载链接
      const link = document.createElement('a');
      link.download = `drawing-${Date.now()}.png`;
      link.href = mergedImage;
      link.click();
      
      toast.success('图片已导出');
    } catch (err) {
      console.error('导出图片失败:', err);
      toast.error('导出失败，请重试');
    }
  };

  // 切换工具
  const handleToolChange = (newTool) => {
    if (tool === 'text' && newTool !== 'text') {
      commitActiveText();
    }

    setTool(newTool);

    if (canvasRef.current) {
      if (newTool === 'eraser') {
        canvasRef.current.eraseMode(true);
      } else {
        canvasRef.current.eraseMode(false);
      }
    }
  };

  // 改变颜色
  const handleColorChange = (color) => {
    setStrokeColor(color);
    if (tool === 'eraser') {
      setTool('pen');
      if (canvasRef.current) {
        canvasRef.current.eraseMode(false);
      }
    }

    if (tool === 'text' && activeText) {
      setActiveText((prev) => (prev ? { ...prev, color } : prev));
    }
  };

  // 点击背景关闭
  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('DrawingModal-backdrop')) {
      onClose();
    }
  };

  // 阻止事件传播到背后的画布，但允许表单控件正常交互
  const stopPropagation = (event) => {
    event.stopPropagation();

    const targetTag = event.target?.tagName || '';
    const interactiveTags = ['INPUT', 'TEXTAREA', 'BUTTON', 'SELECT', 'LABEL'];

    if (!interactiveTags.includes(targetTag) && event.cancelable) {
      event.preventDefault();
    }
  };

  const handleCanvasClick = (event) => {
    if (tool !== 'text') {
      return;
    }

    commitActiveText();

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const newAnnotation = {
      id: nanoid(),
      x,
      y,
      text: '',
      color: strokeColor,
      size: textSize,
    };

    setActiveText(newAnnotation);
    setTextInputValue('');
  };

  const handleTextAnnotationClick = (annotation, event) => {
    event.stopPropagation();
    commitActiveText();
    if (tool !== 'text') {
      handleToolChange('text');
    }
    if (annotation.color) {
      setStrokeColor(annotation.color);
    }
    if (annotation.size) {
      setTextSize(annotation.size);
    }
    setActiveText(annotation);
    setTextInputValue(annotation.text);
  };

  const handleTextSubmit = () => {
    commitActiveText();
  };

  const handleTextCancel = () => {
    setActiveText(null);
    setTextInputValue('');
  };

  const handleTextInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleTextSubmit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleTextCancel();
    }
  };

  const mergeDrawingWithText = (baseImage, annotations) => {
    if (!annotations || annotations.length === 0) {
      return Promise.resolve(baseImage);
    }

    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvasWidth;
        tempCanvas.height = canvasHeight;
        const ctx = tempCanvas.getContext('2d');

        if (!ctx) {
          reject(new Error('无法创建画布上下文'));
          return;
        }

        ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);

        annotations.forEach((annotation) => {
          if (!annotation.text) {
            return;
          }

          ctx.fillStyle = annotation.color || '#000000';
          const fontSize = annotation.size || 24;
          ctx.font = `${fontSize}px "PingFang SC", "Microsoft YaHei", sans-serif`;
          ctx.textBaseline = 'top';
          const lines = annotation.text.split('\n');
          const lineHeight = fontSize * 1.2;

          lines.forEach((line, index) => {
            ctx.fillText(line, annotation.x, annotation.y + index * lineHeight);
          });
        });

        resolve(tempCanvas.toDataURL('image/png'));
      };

      image.onerror = (err) => reject(err);
      image.src = baseImage;
    });
  };

  const modalContent = (
    <div 
      className="DrawingModal-backdrop" 
      onClick={handleBackdropClick}
      onMouseDown={stopPropagation}
      onMouseMove={stopPropagation}
      onMouseUp={stopPropagation}
      onPointerDown={stopPropagation}
      onPointerMove={stopPropagation}
      onPointerUp={stopPropagation}
      onPointerCancel={stopPropagation}
      onTouchStart={stopPropagation}
      onTouchMove={stopPropagation}
      onTouchEnd={stopPropagation}
      onWheel={stopPropagation}
    >
      <div 
        className="DrawingModal-container"
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onPointerDown={stopPropagation}
        onPointerMove={stopPropagation}
        onPointerUp={stopPropagation}
        onPointerCancel={stopPropagation}
        onTouchStart={stopPropagation}
        onTouchMove={stopPropagation}
        onTouchEnd={stopPropagation}
        onWheel={stopPropagation}
      >
        {/* 标题栏 */}
        <div className="DrawingModal-header">
          <h3 className="DrawingModal-title">✏️ 手绘画板</h3>
          <button 
            className="DrawingModal-close"
            onClick={onClose}
            title="关闭"
          >
            ✕
          </button>
        </div>

        {/* 工具栏 */}
        <div className="DrawingModal-toolbar">
          {/* 左侧工具 */}
          <div className="DrawingModal-toolbar-left">
            {/* 工具选择 */}
            <div className="DrawingModal-tool-group">
              <button
                className={`DrawingModal-tool-btn ${tool === 'pen' ? 'DrawingModal-tool-btn-active' : ''}`}
                onClick={() => handleToolChange('pen')}
                title="画笔"
              >
                ✏️ 画笔
              </button>
              <button
                className={`DrawingModal-tool-btn ${tool === 'eraser' ? 'DrawingModal-tool-btn-active' : ''}`}
                onClick={() => handleToolChange('eraser')}
                title="橡皮擦"
              >
                🧹 橡皮擦
              </button>
              <button
                className={`DrawingModal-tool-btn ${tool === 'text' ? 'DrawingModal-tool-btn-active' : ''}`}
                onClick={() => handleToolChange('text')}
                title="文本"
              >
                🔤 文本
              </button>
            </div>

            {/* 粗细调节 */}
            <div className="DrawingModal-slider-group">
              <label className="DrawingModal-label">{sliderLabel}</label>
              <input
                type="range"
                min={sliderMin}
                max={sliderMax}
                value={sliderValue}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (tool === 'pen') {
                    setStrokeWidth(value);
                  } else if (tool === 'eraser') {
                    setEraserWidth(value);
                  } else {
                    setTextSize(value);
                    setActiveText((prev) => (prev ? { ...prev, size: value } : prev));
                  }
                }}
                className="DrawingModal-slider"
              />
            </div>
          </div>

          {/* 右侧操作 */}
          <div className="DrawingModal-toolbar-right">
            <button onClick={handleUndo} className="DrawingModal-action-btn" title="撤销">
              ↶ 撤销
            </button>
            <button onClick={handleRedo} className="DrawingModal-action-btn" title="重做">
              ↷ 重做
            </button>
            <button onClick={handleClear} className="DrawingModal-action-btn DrawingModal-action-btn-danger" title="清空">
              🗑️ 清空
            </button>
            <button onClick={handleExport} className="DrawingModal-action-btn DrawingModal-action-btn-success" title="导出">
              💾 导出
            </button>
          </div>
        </div>

        {/* 颜色选择（画笔或文字模式时显示） */}
        {(tool === 'pen' || tool === 'text') && (
          <div className="DrawingModal-color-bar">
            <label className="DrawingModal-color-label">颜色：</label>
            <input
              type="color"
              value={strokeColor}
              onChange={(e) => handleColorChange(e.target.value)}
              className="DrawingModal-color-picker"
            />
            <div className="DrawingModal-preset-colors">
              {presetColors.map((color) => (
                <button
                  key={color}
                  className={`DrawingModal-preset-color ${strokeColor === color ? 'DrawingModal-preset-color-active' : ''}`}
                  style={{ 
                    backgroundColor: color, 
                    border: color === '#FFFFFF' ? '2px solid #dee2e6' : 'none' 
                  }}
                  onClick={() => handleColorChange(color)}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}

        {/* 画布区域 */}
        <div className="DrawingModal-canvas-wrapper">
          <div
            className="DrawingModal-canvas-container"
            style={{ width: `${canvasWidth}px`, height: `${canvasHeight}px` }}
          >
            <ReactSketchCanvas
              ref={canvasRef}
              width={`${canvasWidth}px`}
              height={`${canvasHeight}px`}
              strokeColor={strokeColor}
              strokeWidth={tool === 'pen' ? strokeWidth : eraserWidth}
              eraserWidth={eraserWidth}
              canvasColor="#ffffff"
              style={{
                border: 'none',
                borderRadius: '8px',
                pointerEvents: tool === 'text' ? 'none' : 'auto',
              }}
              allowOnlyPointerType="all"
            />

            {textAnnotations.map((annotation) => (
              activeText && activeText.id === annotation.id ? null : (
                <div
                  key={annotation.id}
                  className="DrawingModal-text-annotation"
                  style={{
                    left: `${annotation.x}px`,
                    top: `${annotation.y}px`,
                    color: annotation.color || '#000000',
                    fontSize: `${annotation.size || 24}px`,
                  }}
                  onClick={(event) => handleTextAnnotationClick(annotation, event)}
                >
                  {annotation.text}
                </div>
              )
            ))}

            {tool === 'text' && (
              <div
                className="DrawingModal-text-capture"
                onClick={handleCanvasClick}
              />
            )}

            {activeText && (
              <textarea
                ref={textInputRef}
                className="DrawingModal-text-input"
                style={{
                  left: `${activeText.x}px`,
                  top: `${activeText.y}px`,
                  color: activeText.color || '#000000',
                  fontSize: `${activeText.size || textSize}px`,
                }}
                value={textInputValue}
                onChange={(e) => setTextInputValue(e.target.value)}
                onBlur={handleTextSubmit}
                onKeyDown={handleTextInputKeyDown}
                placeholder="输入文字，回车确认，Esc取消"
              />
            )}
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="DrawingModal-footer">
          <button 
            className="DrawingModal-btn DrawingModal-btn-cancel"
            onClick={onClose}
          >
            取消
          </button>
          <button 
            className="DrawingModal-btn DrawingModal-btn-save"
            onClick={handleSave}
          >
            保存绘图
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

